import { z } from 'zod/v4';

/** A claim the owner stands behind. */
export const CLAIM_EXPORT_STATUSES = ['APPROVED', 'NOT_VERIFIABLE'] as const;

/**
 * The `certifications.type` values that count as a certification, and the
 * groups the picker nests by. In contracts because the dialog resolves a label
 * per type through a computed i18n key, which the locale parity test checks.
 *
 * `standards.type` uses a disjoint vocabulary (`BADGE`, `PAYMENT`), so a
 * standards-backed claim never matches — badges and payment proofs are not
 * certificates.
 */
export const CLAIM_EXPORT_CERTIFICATION_TYPES = [
  'CERTIFICATE',
  'MEMBERSHIP',
  'PRODUCT',
  'SCOPE',
  'COMPANY',
] as const;

/**
 * A certification standard or audit type the exported network actually holds.
 *
 * `companiesCount` mirrors the data point catalogue's `answeredCompaniesCount`
 * so the picker can render every branch the same way.
 */
export const ExportNetworkAssetSummarySchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  /**
   * Certifications: the standard's type, which the picker nests by. Audits: the
   * audit type's category, or null — those render flat.
   */
  group: z.string().nullable(),
  companiesCount: z.number().int(),
});

export type ExportNetworkAssetSummary = z.infer<
  typeof ExportNetworkAssetSummarySchema
>;

/**
 * One asset branch of the picker.
 *
 * `permitted: false` with empty lists is what a caller without the read
 * permission gets. Returning that rather than failing the whole request lets the
 * dialog show a disabled branch, and it avoids naming standards or audit types
 * to someone who may not see them.
 */
export const ExportNetworkAssetCatalogueSchema = z.object({
  permitted: z.boolean(),
  items: z.array(ExportNetworkAssetSummarySchema),
});

export type ExportNetworkAssetCatalogue = z.infer<
  typeof ExportNetworkAssetCatalogueSchema
>;

export const EMPTY_ASSET_CATALOGUE: ExportNetworkAssetCatalogue = {
  permitted: false,
  items: [],
};
